import { InputGs, SelectGs } from 'components/ui/Forms';
import { Button } from 'components/ui/button';
import { Switch } from 'components/ui/switch';
import { Label } from 'components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/ui/table';
import { Info, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useProductUnit } from 'hooks/api/general/useProductUnit';
import { useProduct } from 'hooks/api/general/useProduct';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { Autocomplete } from '@mantine/core';
import { DataProduct, useFiles } from 'hooks/files';
import type { Product } from 'contracts/Products';
import { DetailRef, ItemDetail } from '../ItemDetail';

interface UnloadFormProps {
  addProduct: (product: any) => void;
  removeProduct: (productId: string) => void;
  updateProduct: (product: any) => void;
  productList: any[];
  companies: any[];
  companyIe: any[];
}

export const UnloadForm: React.FC<UnloadFormProps> = ({
  addProduct,
  removeProduct,
  updateProduct,
  productList,
  companyIe,
  companies,
}) => {
  const [productCode, setProductCode] = useState('');
  const [stepQuantity, setStepQuantity] = useState<number>(0);
  const [productName, setProductName] = useState('');
  const [productQuantity, setProductQuantity] = useState(0);
  const [productUnit, setProductUnit] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [isAutoImport, setIsAutoImport] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[] | null>(null);
  const [filePlaceHolder, setFilePlaceHolder] = useState(
    'Selecione o cliente...'
  );
  const { getUnits } = useProductUnit();
  const { getProducts } = useProduct();
  const { readFiles } = useFiles();
  const { checkProduct } = useProduct();

  const detailRef = useRef<DetailRef>(null);

  const companyIds = useMemo(
    () => companies.map(({ value }) => value),
    [companies]
  );

  const { data: units, isPending } = useQuery({
    queryKey: ['productUnits'],
    queryFn: () => getUnits(),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const productCheckQueries = useQueries({
    queries: productList.map((product) => ({
      queryKey: ['checkProduct', product.code, ...companyIds],
      queryFn: () => checkProduct({ code: product.code, companyIds }),
      staleTime: 3e4,
    })),
  });

  const addProductsByXML = (dataProduct: Array<DataProduct | null>) => {
    const productsToAdd: Partial<Product>[] = [];

    dataProduct.forEach((data) => {
      try {
        if (!data) return;

        let errorMessage = '';

        const emitIE = Number(data.emit_IE).toString();
        const destIE = Number(data.dest_IE).toString();
        const destDocument = data.dest_CNPJ || data.dest_CPF;

        //SE O DESTINATARIO FOR COOPERFIBRA, VERIFICA SE O EMITENTE É O CLIENTE
        if (
          '04476442000402'.includes(destDocument) &&
          !'04476442000402'.includes(emitIE)
        ) {
          const emitIsCompany = companyIe.some((company) =>
            company.ie?.includes(emitIE)
          );

          if (!emitIsCompany || !emitIE) {
            errorMessage = `NF (${data.nNF}) A IE do emitente não corresponde à IE do cliente`;
          }
        }

        //SE O DESTINATARIO NÃO FOR COOPERFIBRA, VERIFICA SE O DESTINATARIO É O CLIENTE
        if (!'04476442000402'.includes(destDocument)) {
          const destIsCompany = companyIe.some((company) =>
            company.ie?.includes(destIE)
          );

          if (!destIsCompany || !destIE) {
            errorMessage = `NF (${data.nNF}) IE do destinatário não corresponde à IE do cliente`;
          }
        }

        if (errorMessage) {
          toast.error(errorMessage);
          return reset();
        }

        const companyId =
          companyIe.find((company) => company.ie?.includes(emitIE))?.id ||
          companyIe.find((company) => company.ie?.includes(destIE)).id;

        const companyName =
          companies.find(
            (company) => String(company.value) === String(companyId)
          )?.name || '';

        data.items.forEach((item) => {
          const product = {
            id: item.cProd + companyId + crypto.randomUUID(), // Example tag names
            code: item.cProd,
            description: item.xProd + ' - ' + companyName,
            quantity: parseInt(item.qCom, 10),
            unit: item.uCom,
            companyId: companyId,
            nfNumber: data.nNF,
          };

          productsToAdd.push(product);
        });
      } catch (error) {
        console.log('Error adding products', error);
      }
    });

    addProduct(productsToAdd);
  };

  const mutationFile = useMutation({
    mutationFn: readFiles,
    onSuccess: addProductsByXML,
  });

  const {
    data: { productOptions, products } = {},
    isPending: isPendingProducts,
  } = useQuery({
    queryKey: ['products'],
    queryFn: () => getProducts(),
    staleTime: 3e5,
    refetchOnWindowFocus: false,
  });

  const onChangeProduct = (value: string) => {
    const findedProduct = products?.find(({ name }) => name === value);

    if (findedProduct) setStepQuantity(findedProduct.factor);
    else setStepQuantity(0);

    setProductQuantity(0);
    setProductName(value);
  };

  const onBlurQuantity = () => {
    if (stepQuantity) {
      setProductQuantity((prev) =>
        prev % stepQuantity !== 0 ? prev - (prev % stepQuantity) : prev
      );
    }
  };

  useEffect(() => {
    if (companyIe) {
      setFilePlaceHolder('Selecione um arquivo');
    }
    if (selectedFiles) {
      setFilePlaceHolder(selectedFiles.map((file) => file.name).join(' / '));
    }
  }, [selectedFiles, companyIe]);

  const handleAddProductManual = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();

    const productToAdd = {
      id: productCode + companyId,
      code: productCode,
      description:
        productName +
        ' - ' +
        companies.filter((company) => company.value === companyId)[0].name,
      quantity: productQuantity,
      volume: stepQuantity ? productQuantity / stepQuantity : 0,
      unit: productUnit,
      companyId: companyId,
    };

    addProduct([productToAdd]);
    reset();
  };

  const reset = () => {
    setProductCode('');
    setProductName('');
    setStepQuantity(0);
    setProductQuantity(0);
    setProductUnit('');
    setCompanyId('');
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (files?.length) {
      setSelectedFiles([...files]);
    } else {
      setSelectedFiles(null); // Garantir que o estado é limpo se não há arquivo
    }

    event.target.value = '';
  };

  const onChangeValue = (event: any, product: any) => {
    const newProduct = {
      ...product,
      volume: event.target.value,
    };
    updateProduct(newProduct);
  };

  const handleFileUpload = (event: any) => {
    event.preventDefault();

    if (selectedFiles?.length) {
      mutationFile.mutate(selectedFiles);
    }

    setSelectedFiles(null);
  };

  const calcVolume = (product: any, factor?: number) => {
    if (product.volume) return product.volume;

    if (!product.quantity || !factor) return 0;

    const volume = product.quantity / factor;

    product.volume = volume;

    return volume;
  };

  return (
    <div className="w-full p-6 rounded-sm bg-gray-100 my-1">
      <ItemDetail ref={detailRef} />
      <div className="flex items-center space-x-2 border-b border-b-slate-300 pb-4 mb-2">
        <Switch
          id="autoimport"
          checked={isAutoImport}
          onCheckedChange={() => setIsAutoImport(!isAutoImport)}
        />
        <Label htmlFor="autoimport">Automática</Label>
      </div>
      {isAutoImport && (
        <>
          <div className="mb-4">
            <h1 className="font-GilroyBold text-lg">Produtos</h1>
            <p className="font-sans text-sm">
              Faça o upload do XML da nota fiscal para preencher os produtos
            </p>
          </div>
          <div className="mb-4">
            <p className="text-sm text-gray-600 font-GilroySemibold">
              XML da NFe
            </p>
            <div className="grid grid-cols-1 md:grid-cols-10 gap-4 mt-1 items-end">
              <div className="col-span-1 md:col-span-8 xl:col-span-9">
                <InputGs
                  type="file"
                  id="fileInput"
                  accept=".xml"
                  multiple
                  onChange={handleFileChange}
                  className="opacity-0 absolute w-full top-0 left-0"
                  disabled={!companyIe?.length}
                />
                <label
                  htmlFor="fileInput"
                  onClick={() =>
                    !companyIe?.length && toast.error('Selecione o cliente')
                  }
                  className={`block relative overflow-hidden rounded-md border border-slate-200 px-3 py-2 h-10 text-sm ${companyIe ? 'cursor-pointer bg-white' : 'cursor-not-allowed bg-gray-200'}`}
                >
                  <span
                    title={`${filePlaceHolder.replaceAll(' / ', '\n')}`}
                    id="fileName"
                    className="text-sm text-slate-500"
                  >
                    {filePlaceHolder}
                  </span>
                  {selectedFiles?.length ? (
                    <span
                      title={`${selectedFiles.length} Arquivos`}
                      className="absolute right-3 top-2 text-sm text-slate-500 border border-transparent border-l-slate-300 pl-3"
                    >
                      {selectedFiles?.length}
                    </span>
                  ) : (
                    <></>
                  )}
                </label>
              </div>
              <div className="col-span-1 md:col-span-2 xl:col-span-1">
                <Button
                  className="w-full"
                  type="button"
                  disabled={
                    !selectedFiles || mutationFile.isPending || !companyIe
                  }
                  onClick={handleFileUpload}
                >
                  Adicionar
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
      {!isAutoImport && (
        <>
          <div>
            <h1 className="font-GilroyBold text-lg">Produtos</h1>
            <p className="font-sans text-sm">
              Adicione produtos à sua solicitação de carregamento
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 xl:grid-cols-12 gap-4 mt-3 items-end">
            <div className="col-span-1 md:col-span-2">
              <InputGs
                name="code"
                label="Código do produto"
                type="text"
                value={productCode}
                onChange={(e) => setProductCode(e.target.value)}
              />
            </div>
            <div className="col-span-1 md:col-span-3 xl:col-span-3">
              {/* https://mantine.dev/core/autocomplete/ */}
              <Autocomplete
                label="Descrição do produto"
                placeholder=""
                data={productOptions || []}
                value={productName}
                onChange={onChangeProduct}
                styles={{
                  root: {
                    display: 'grid',
                    gap: '6px',
                    alignItems: 'center',
                  },
                  dropdown: {
                    borderRadius: '6px',
                    borderColor: 'rgb(226, 232, 240)',
                  },
                  input: {
                    borderRadius: '6px',
                    height: '40px',
                    borderColor: 'rgb(226, 232, 240)',
                  },
                  label: {
                    color: 'rgb(30,41,59)',
                    fontWeight: 500,
                    fontFamily: 'Gilroy-SemiBold',
                    fontSize: '0.875rem',
                    lineHeight: '1.25rem',
                  },
                }}
              />
            </div>
            <div className="col-span-1 md:col-span-3">
              <SelectGs
                name="companyId"
                label="Cliente"
                content={companies || []}
                value={companyId}
                onValueChange={(value) => setCompanyId(value)}
                isLoading={isPending || companies.length === 0}
              />
            </div>
            <div className="col-span-1 md:col-span-1">
              <InputGs
                name="quantity"
                label="Quantidade"
                type="number"
                step={stepQuantity}
                disabled={!productName}
                onBlur={onBlurQuantity}
                min={0}
                value={productQuantity}
                onChange={(e) => setProductQuantity(Number(e.target.value))}
              />
            </div>
            <div className="col-span-1 md:col-span-1">
              <InputGs
                name="volume"
                label="Volume"
                type="number"
                value={stepQuantity ? productQuantity / stepQuantity : 0}
                disabled
              />
            </div>
            <div className="col-span-1 md:col-span-1">
              <SelectGs
                name="unit"
                label="Unidade"
                content={units || []}
                value={productUnit}
                onValueChange={(value) => setProductUnit(value)}
                isLoading={isPending}
              />
            </div>
            <div className="col-span-1 md:col-span-2 xl:col-span-1">
              <Button
                className="w-full"
                disabled={!productCode || !productName || !productQuantity}
                onClick={handleAddProductManual}
              >
                Adicionar
              </Button>
            </div>
          </div>
        </>
      )}
      {productList.length > 0 && (
        <div className="w-full mt-6">
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[100px]">Código</TableHead>
                <TableHead className="w-[100px]">NF</TableHead>
                <TableHead>Descrição</TableHead>
                <TableHead></TableHead>
                <TableHead>Quantidade</TableHead>
                <TableHead>Volume</TableHead>
                <TableHead>Unidade</TableHead>
                <TableHead className="text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {productList.map((product, i) => (
                <TableRow key={product.id}>
                  <TableCell className="font-medium">{product.code}</TableCell>
                  <TableCell className="font-medium">
                    {product.nfNumber}
                  </TableCell>
                  <TableCell className="font-medium">
                    {product.description}
                  </TableCell>
                  <TableCell>
                    {productCheckQueries[i].data?.isUsed ? (
                      <button
                        title="Este produto está sendo usado"
                        type="button"
                        onClick={() =>
                          detailRef.current?.showDetail(
                            product.code,
                            companyIds
                          )
                        }
                      >
                        <Info
                          color="#dc2626"
                          size={18}
                          className="transition-all hover:stroke-red-700"
                        />
                      </button>
                    ) : productCheckQueries[i].isPending ? (
                      <div
                        title="Verificando relações de agendamentos..."
                        className="border-2 border-white border-l-green-700 animate-spin w-4 h-4 rounded-3xl"
                      />
                    ) : (
                      <></>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">
                    {product.quantity}
                  </TableCell>
                  <TableCell className="font-medium">
                    {!isAutoImport ? (
                      product.volume
                    ) : (
                      <InputGs
                        type="number"
                        onChange={(e) => onChangeValue(e, product)}
                        value={calcVolume(
                          product,
                          productCheckQueries[i].data?.factor
                        )}
                        min={0}
                      />
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{product.unit}</TableCell>
                  <TableCell>
                    <Trash2
                      className="text-red-600 cursor-pointer"
                      onClick={() => removeProduct(product.id)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
